import { Button, Checkbox, DatePicker, Textfield, token } from "@may-i/mash-design-system";
import { type ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import styled, { createGlobalStyle, css, keyframes } from "styled-components";
import type { BranchVersion } from "~/entities/branch-version/types";
import { useUpdateBranchVersion } from "~/features/branch-version/hooks/use-branch-version-mutations";
import { useRestoreBodyPointerEvents } from "~/shared/hooks/use-restore-body-pointer-events";
import { useToast } from "~/shared/ui/toast";

const NAME_MAX_LENGTH = 50;
const DESCRIPTION_MAX_LENGTH = 200;

/** timestamp → "YYYY-MM-DD" (로컬 타임존 기준) */
function formatLocalDate(ts: number): string {
  const d = new Date(ts);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatPickerDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}.${m}.${day}`;
}

function formatPickerPeriod(start?: Date, end?: Date): string {
  const s = start ? formatPickerDate(start) : "";
  const e = end ? formatPickerDate(end) : "";
  if (s && e) return s === e ? s : `${s} - ${e}`;
  if (s) return s;
  return e;
}

interface EditVersionDrawerProps {
  open: boolean;
  version: BranchVersion | null;
  onClose: () => void;
}

export function EditVersionDrawer({
  open,
  version,
  onClose,
}: EditVersionDrawerProps) {
  const { t } = useTranslation("version");
  const { branchUid } = useParams();
  const updateBranchVersion = useUpdateBranchVersion(branchUid);
  const { showToast } = useToast();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [validFrom, setValidFrom] = useState<string>("");
  const [validTo, setValidTo] = useState<string>("");
  const [noEndDate, setNoEndDate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Populate form when version changes
  useEffect(() => {
    if (version) {
      setName(version.name);
      setDescription(version.description);
      setValidFrom(version.validFrom ?? "");
      setValidTo(version.validTo ?? "");
      setNoEndDate(!version.validTo);
    }
  }, [version]);

  const trimmedName = name.trim();
  const isFormValid =
    trimmedName.length > 0 && validFrom !== "" && (noEndDate || validTo !== "");
  const isDirty = version
    ? name !== version.name
      || description !== version.description
      || validFrom !== (version.validFrom ?? "")
      || (noEndDate ? !!version.validTo : validTo !== (version.validTo ?? ""))
    : false;

  // Animation state
  const [shouldRender, setShouldRender] = useState(false);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      requestAnimationFrame(() => setAnimating(true));
    } else {
      setAnimating(false);
      const timer = setTimeout(() => setShouldRender(false), 250);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // B7: 드로어가 내부 Radix 레이어 열린 채 닫힐 때 body pointer-events:none 잔존 방어
  useRestoreBodyPointerEvents(open);

  const resetForm = () => {
    setName("");
    setDescription("");
    setValidFrom("");
    setValidTo("");
    setNoEndDate(false);
  };

  const handleClose = () => {
    if (isDirty && !isSubmitting) {
      const ok = window.confirm(
        `${t("discardConfirm.title")}\n\n${t("discardConfirm.message")}`,
      );
      if (!ok) return;
    }
    resetForm();
    onClose();
  };

  const handleSave = async () => {
    if (!version || !isFormValid || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await updateBranchVersion.mutateAsync({
        branchVersionId: version.id,
        name: trimmedName,
        description: description.trim(),
        validFrom,
        validTo: noEndDate ? "" : validTo,
      });
      showToast({
        type: "success",
        title: t("toast.editSuccess.title"),
        content: t("toast.editSuccess.content"),
      });
      resetForm();
      onClose();
    } catch (err) {
      console.error("[EditVersionDrawer] save failed:", err);
      showToast({
        type: "error",
        title: t("toast.editError.title"),
        content: t("toast.editError.content"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!shouldRender) return null;

  return (
    <>
      <DatePickerPortalStyle />
      <Overlay $visible={animating} onClick={handleClose}>
        <Panel $visible={animating} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <Header>
            <HeaderTitle>{t("editDrawer.title")}</HeaderTitle>
          </Header>

          {/* Body */}
          <Body>
            {/* 이름 */}
            <FieldGroup>
              <FieldLabel>{t("editDrawer.field.name")}</FieldLabel>
              <Textfield
                size="xSmall"
                placeholder={t("editDrawer.placeholder.name")}
                value={name}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setName(e.target.value.slice(0, NAME_MAX_LENGTH))
                }
              />
            </FieldGroup>

            {/* 설명 */}
            <FieldGroup>
              <FieldLabel>{t("editDrawer.field.description")}</FieldLabel>
              <Textfield
                size="xSmall"
                placeholder={t("editDrawer.placeholder.description")}
                value={description}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setDescription(e.target.value.slice(0, DESCRIPTION_MAX_LENGTH))
                }
              />
            </FieldGroup>

            {/* 유효 기간 */}
            <FieldGroup>
              <FieldLabel>{t("editDrawer.field.validPeriod")}</FieldLabel>
              <ValidPeriodRow>
                <DatePickerSlot>
                  <DatePicker.Root
                    autoFillEndDate
                    key={String(noEndDate)}
                    enabledRange={!noEndDate}
                    startDate={validFrom || undefined}
                    endDate={noEndDate ? undefined : (validTo || undefined)}
                    onConfirm={(start?: number, end?: number) => {
                      setValidFrom(
                        start != null
                          ? formatLocalDate(start)
                          : "",
                      );
                      if (!noEndDate) {
                        const effectiveEnd = end ?? start;
                        setValidTo(
                          effectiveEnd != null
                            ? formatLocalDate(effectiveEnd)
                            : "",
                        );
                      }
                    }}
                  >
                    <StyledCalendarToggle
                      variant="outlined"
                      placeholder={t("editDrawer.placeholder.validPeriod")}
                      formatPeriod={formatPickerPeriod}
                    >
                      <StyledCalendar presets={[]} />
                    </StyledCalendarToggle>
                  </DatePicker.Root>
                </DatePickerSlot>
                <CheckboxSlot>
                  <Checkbox
                    size="small"
                    label={t("editDrawer.noEndDate")}
                    checked={noEndDate}
                    onCheckedChange={(checked) => {
                      setNoEndDate(checked === true);
                      if (checked) setValidTo("");
                      else setValidTo(validFrom);
                    }}
                  />
                </CheckboxSlot>
              </ValidPeriodRow>
            </FieldGroup>
          </Body>

          {/* Footer */}
          <Footer>
            <Button
              variant="outlined"
              color="primary"
              size="medium"
              onClick={handleClose}
            >
              {t("editDrawer.cancel")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="medium"
              onClick={handleSave}
              disabled={!isFormValid || !isDirty || isSubmitting}
            >
              {t("editDrawer.save")}
            </Button>
          </Footer>
        </Panel>
      </Overlay>
    </>
  );
}

// --- Animations ---

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;

const slideOut = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(100%); }
`;

// --- Styles ---

const Overlay = styled.div<{ $visible: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 100;
  background-color: ${({ $visible }) =>
    $visible ? "rgba(0, 0, 0, 0.2)" : "transparent"};
  transition: background-color 0.25s ease;
  /* B7 별건: 닫힘 애니메이션(250ms) 동안 투명 오버레이가 클릭을 삼키지 않도록 게이팅 */
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
`;

const Panel = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 480px;
  display: flex;
  flex-direction: column;
  background-color: ${token("colors.gray.white")};
  box-shadow: -10px 2px 20px 0px rgba(0, 0, 0, 0.12);
  animation: ${({ $visible }) =>
      $visible
        ? css`${slideIn}`
        : css`${slideOut}`}
    0.25s ease forwards;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 24px;
`;

const HeaderTitle = styled.h3`
  margin: 0;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.56;
  color: ${token("colors.text.primary")};
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 24px;
  flex: 1;
  overflow-y: auto;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 24px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ValidPeriodRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DatePickerSlot = styled.div`
  width: 100%;
`;

const CheckboxSlot = styled.div`
  align-self: flex-end;
  white-space: nowrap;
  & * {
    white-space: nowrap;
  }
`;

const FieldLabel = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  color: ${token("colors.text.secondary")};
`;

// --- DatePicker ---

const DatePickerPortalStyle = createGlobalStyle`
  [data-radix-popper-content-wrapper] {
    z-index: 101 !important;
  }
`;

const StyledCalendarToggle = styled(DatePicker.CalendarToggle)`
  --date-picker-trigger-width: 100%;
`;

const StyledCalendar = styled(DatePicker.Calendar)`
  && {
    max-height: var(--radix-popover-content-available-height, 80vh);
  }
  box-shadow: ${token("shadows.dropdown")};

  /* month picker button 텍스트 중앙 정렬 보정 */
  ol li button {
    text-align: center;
  }
`;
